import { memo } from 'react';
import { Handle, Position, NodeResizer } from '@xyflow/react';
import { Building } from 'lucide-react';
import { getCriticalityColor, normalizeCriticality } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';

interface SiteGroupNodeData {
  id: string;
  label: string;
  criticality?: string;
  siteCode?: string;
}

interface SiteGroupNodeProps {
  data: SiteGroupNodeData;
  selected: boolean;
}

export const SiteGroupNode = memo(({ data, selected }: SiteGroupNodeProps) => {
  return (
    <>
      <NodeResizer
        minWidth={400}
        minHeight={300}
        isVisible={selected}
        lineClassName="border-primary border-2"
        handleClassName="h-6 w-6 bg-primary rounded-sm border-2 border-background cursor-nwse-resize"
      />
      <div className="w-full h-full rounded-lg border-2 border-slate-400 dark:border-slate-500 bg-slate-50/60 dark:bg-slate-900/60 backdrop-blur-sm">
        <div className="flex items-center gap-2 px-4 py-2 bg-slate-100/70 dark:bg-slate-800/70 rounded-t-lg border-b border-slate-300/50 dark:border-slate-600/50">
          <Building className="h-5 w-5 text-slate-600 dark:text-slate-300" />
          <div className="flex-1">
            <div className="font-semibold text-sm">{data.label}</div>
            {data.siteCode && (
              <div className="text-xs text-muted-foreground">{data.siteCode}</div>
            )}
          </div>
          {data.criticality && (
            <Badge variant="outline" className={getCriticalityColor(data.criticality)}>
              {normalizeCriticality(data.criticality)}
            </Badge>
          )}
        </div>
      </div>

      <Handle id="top" type="source" position={Position.Top} isConnectableStart={true} isConnectableEnd={true} className="w-3 h-3 border-2 border-slate-500 bg-slate-200 dark:bg-slate-600" />
      <Handle id="right" type="source" position={Position.Right} isConnectableStart={true} isConnectableEnd={true} className="w-3 h-3 border-2 border-slate-500 bg-slate-200 dark:bg-slate-600" />
      <Handle id="bottom" type="source" position={Position.Bottom} isConnectableStart={true} isConnectableEnd={true} className="w-3 h-3 border-2 border-slate-500 bg-slate-200 dark:bg-slate-600" />
      <Handle id="left" type="source" position={Position.Left} isConnectableStart={true} isConnectableEnd={true} className="w-3 h-3 border-2 border-slate-500 bg-slate-200 dark:bg-slate-600" />
    </>
  );
});

SiteGroupNode.displayName = 'SiteGroupNode';
